import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import "./FullBlog.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

const FullBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [readingProgress, setReadingProgress] = useState(0);

  // Fetch the blog by ID
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const docRef = doc(db, "blogs", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBlog({ id: docSnap.id, ...docSnap.data() });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching blog:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id]);

  // Fetch related blogs (latest 3, excluding current)
  useEffect(() => {
    const fetchRelated = async () => {
      try {
        const q = query(
          collection(db, "blogs"),
          orderBy("createdAt", "desc"),
          limit(6),
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) => b.isActive === true && b.id !== id)
          .slice(0, 3);
        setRelatedBlogs(data);
      } catch (err) {
        console.error("Error fetching related blogs:", err);
      }
    };
    fetchRelated();
  }, [id]);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setReadingProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const estimateReadTime = (text) => {
    if (!text) return "1 min read";
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  };

  // ── LOADING ──
  if (loading) {
    return (
      <div className="fb-loading-screen">
        <div className="fb-loading-spinner"></div>
        <p>Loading story...</p>
      </div>
    );
  }

  // ── NOT FOUND ──
  if (notFound || !blog) {
    return (
      <div className="fb-notfound">
        <i className="fas fa-map-signs"></i>
        <h2>Story not found</h2>
        <p>This blog may have been removed or the link is invalid.</p>
        <button onClick={() => navigate("/blogs")}>
          <i className="fas fa-arrow-left"></i> Back to Blogs
        </button>
      </div>
    );
  }

  return (
    <div className="fb-page">
      {/* Reading Progress Bar */}
      <div
        className="fb-progress-bar"
        style={{ width: `${readingProgress}%` }}
      />

      {/* ── NAVBAR ── */}
      <Navbar />

      {/* ── HERO / COVER ── */}
      <header className="fb-hero">
        {blog.coverImageUrl ? (
          <img
            className="fb-hero-img"
            src={blog.coverImageUrl}
            alt={blog.title}
          />
        ) : (
          <div className="fb-hero-placeholder">
            <i className="fas fa-globe-asia"></i>
          </div>
        )}
        <div className="fb-hero-overlay">
          {/* Back button */}
          <button className="fb-back-btn" onClick={() => navigate("/blogs")}>
            <i className="fas fa-arrow-left"></i> All Stories
          </button>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="fb-hero-tags">
              {blog.tags.map((tag, i) => (
                <span key={i} className="fb-hero-tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <h1 className="fb-hero-title">{blog.title}</h1>

          {/* Meta row */}
          <div className="fb-hero-meta">
            {blog.authorName && (
              <div className="fb-hero-author">
                <div className="fb-hero-avatar">
                  {blog.authorName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="fb-hero-author-name">{blog.authorName}</span>
                  {blog.authorDesignation && (
                    <span className="fb-hero-author-role">
                      {blog.authorDesignation}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="fb-hero-info">
              {blog.createdAt && (
                <span>
                  <i className="fas fa-calendar-alt"></i>{" "}
                  {formatDate(blog.createdAt)}
                </span>
              )}
              {blog.body && (
                <span>
                  <i className="fas fa-clock"></i> {estimateReadTime(blog.body)}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="fb-main">
        <div className="fb-content-wrap">
          {/* Traveller's Quote */}
          {blog.travellersQuote && (
            <blockquote className="fb-quote">
              <i className="fas fa-quote-left fb-quote-icon"></i>
              <p>{blog.travellersQuote}</p>
              {blog.authorName && <cite>— {blog.authorName}</cite>}
            </blockquote>
          )}

          {/* Blog Body */}
          {blog.body && (
            <div className="fb-body">
              {blog.body
                .split("\n")
                .map((para, i) =>
                  para.trim() ? <p key={i}>{para}</p> : <br key={i} />,
                )}
            </div>
          )}

          {/* Extra images (if any) */}
          {blog.images && blog.images.length > 0 && (
            <div className="fb-gallery">
              <h3 className="fb-gallery-title">
                <i className="fas fa-images"></i> Photo Gallery
              </h3>
              <div className="fb-gallery-grid">
                {blog.images.map((url, i) => (
                  <div className="fb-gallery-item" key={i}>
                    <img src={url} alt={`Gallery ${i + 1}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags footer */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="fb-tags-footer">
              <span className="fb-tags-label">Tags:</span>
              {blog.tags.map((tag, i) => (
                <span
                  key={i}
                  className="fb-tag-pill"
                  onClick={() => navigate("/blogs")}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Author Card */}
          {blog.authorName && (
            <div className="fb-author-card">
              <div className="fb-author-avatar-lg">
                {blog.authorName.charAt(0).toUpperCase()}
              </div>
              <div className="fb-author-info">
                <p className="fb-author-label">Written by</p>
                <h4 className="fb-author-name">{blog.authorName}</h4>
                {blog.authorDesignation && (
                  <p className="fb-author-role">{blog.authorDesignation}</p>
                )}
              </div>
            </div>
          )}

          {/* Back to blogs */}
          <div className="fb-back-row">
            <button
              className="fb-back-all-btn"
              onClick={() => navigate("/blogs")}
            >
              <i className="fas fa-arrow-left"></i> Back to All Stories
            </button>
          </div>
        </div>
      </main>

      {/* ── RELATED BLOGS ── */}
      {relatedBlogs.length > 0 && (
        <section className="fb-related">
          <h2 className="fb-related-title">More Travel Stories</h2>
          <div className="fb-related-grid">
            {relatedBlogs.map((rb) => (
              <div
                className="fb-related-card"
                key={rb.id}
                onClick={() => navigate(`/blogs/${rb.id}`)}
              >
                <div className="fb-related-img">
                  {rb.coverImageUrl ? (
                    <img src={rb.coverImageUrl} alt={rb.title} loading="lazy" />
                  ) : (
                    <div className="fb-related-img-placeholder">
                      <i className="fas fa-image"></i>
                    </div>
                  )}
                </div>
                <div className="fb-related-content">
                  {rb.tags && rb.tags.length > 0 && (
                    <span className="fb-related-tag">#{rb.tags[0]}</span>
                  )}
                  <h4 className="fb-related-card-title">{rb.title}</h4>
                  {rb.authorName && (
                    <p className="fb-related-author">
                      <i className="fas fa-user"></i> {rb.authorName}
                    </p>
                  )}
                  <span className="fb-related-read">
                    Read Story <i className="fas fa-arrow-right"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FOOTER ── */}
      <Footer/>
    </div>
  );
};

export default FullBlog;
